import React, { useState, useMemo, useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import { Stack, Toast, ToastContainer, Form, Badge, Modal, Button, Spinner } from "react-bootstrap";
import { Tooltip } from '@mui/material';
import AdminMaster from "../../../Layout/admin/Master";
import { addDays, addMonths, endOfMonth, endOfWeek, endOfYear, startOfMonth, startOfWeek, startOfYear, subDays, setHours, setMinutes, setSeconds } from "date-fns";
import PartnerDropDown from "../../common/PartnerClientFacilityDropDown/PartnerDropDown";
import ClientDropDown from "../../common/PartnerClientFacilityDropDown/ClientDropDown";
import FacilityDropDown from "../../common/PartnerClientFacilityDropDown/FacilityDropDown";
import FilterComponent from "../../common/FilterComponent";
import TableHeadings from "../../common/TableHeadings";
import DateRangePicker from 'rsuite/DateRangePicker';
import { getFileMergeSchedulerLogs, getFileMergeSchedulerLogHistory, downloadFullS3File } from "../../../Redux/sftpClient/action";
import { getUserlist } from "../../../Redux/qms/action";
import { handleDownloadFileUrl } from "../Dashboard/functions";
import moment from "moment";
import queueDatatableCustomStyles from "../../../Layout/Elements/QueueDatatableCustomStyles";

import { Link } from "react-router-dom";
import { BsClockHistory, BsFileEarmarkText, BsFileEarmarkPdf, BsFileEarmarkImage, BsFileEarmarkZip, BsFileEarmarkSpreadsheet, BsX, BsCheck2, BsDownload } from "react-icons/bs";
import { BiSearch, BiCopy } from "react-icons/bi";
import SchedulerLogModal from "./components/SchedulerLogModal";
import DetailedLoggingModal from "./components/DetailedLoggingModal";
import Select from 'react-select';
import { useFormik } from "formik";
import 'rsuite/dist/rsuite.min.css';

const predefinedBottomRanges = [
    {
        label: 'Today',
        value: [new Date().setHours(0, 0, 0, 0), new Date().setHours(23, 59, 59, 59)],
        placement: 'left'
    },
    {
        label: 'Yesterday',
        value: [addDays(new Date().setHours(0, 0, 0, 0), -1), addDays(new Date().setHours(23, 59, 59, 59), -1)],
        placement: 'left'       
    },
    {
        label: 'This week',
        value: [startOfWeek(new Date().setHours(0, 0, 0, 0)), new Date()],
        placement: 'left'
    },
    {
        label: 'Last 7 days',
        value: [subDays(new Date().setHours(0, 0, 0, 0), 6), new Date()],
        placement: 'left'
    },
    {
        label: 'Last 30 days',
        value: [subDays(new Date().setHours(0, 0, 0, 0), 29), new Date()],
        placement: 'left'
    },
    {
        label: 'This month',
        value: [startOfMonth(new Date().setHours(0, 0, 0, 0)), new Date()],
        placement: 'left'
    },
    {
        label: 'Last month',
        value: [startOfMonth(addMonths(new Date().setHours(0, 0, 0, 0), -1)), endOfMonth(addMonths(new Date(), -1))],
        placement: 'left'
    },
    {
        label: 'This year',
        value: [new Date(new Date().getFullYear(), 0, 1).setHours(0, 0, 0, 0), new Date()],
        placement: 'left'
    },
    {
        label: 'Last year',
        value: [setSeconds(setMinutes(setHours(startOfYear(new Date(new Date().getFullYear() - 1, 0, 1)), 0), 0), 0), setSeconds(setMinutes(setHours(endOfYear(new Date(new Date().getFullYear(), 0, 0)), 23), 59), 59)],
        placement: 'left'
    }
];

const customReactSelectStyles = {
    container: (styles) => ({
        ...styles,
        borderRadius: "9px"
    }),
    placeholder: (styles) => ({
        ...styles,
        fontSize: '17px',
        color: '#6c757d'
    }),
    control: (styles, state) => ({
        ...styles,
        backgroundColor: "hsl(204deg 33.33% 97.06%)",
        borderStyle: "none",
        boxShadow: "none",
        borderRadius: "10px",
        minHeight: "38px"
    }),
    indicatorSeparator: (styles) => ({
        ...styles,
        width: "0px",
        minHeight: "20px"
    })
};

const ACCENT = '#6ba2b6';
const ACCENT_DARK = '#4f7f91';

const mergeLogsTableStyles = {
    tableWrapper: {
        style: {
            borderRadius: '14px',
            overflow: 'hidden',
            border: '1px solid #eef2f5',
        },
    },
    header: {
        style: {
            minHeight: '56px',
        },
    },
    headRow: {
        style: {
            borderTopStyle: 'solid',
            borderTopWidth: '1px',
            borderTopColor: '#eef2f5',
            borderBottomStyle: 'solid',
            borderBottomWidth: '2px',
            borderBottomColor: ACCENT,
            backgroundColor: '#f4f8f9',
            minHeight: '46px',
        },
    },
    headCells: {
        style: {
            fontSize: '11px',
            fontWeight: '700',
            color: '#2f4650',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            justifyContent: 'flex-start',
            paddingLeft: '14px',
            paddingRight: '14px',
        },
    },
    rows: {
        style: {
            minHeight: '48px',
            borderBottomStyle: 'solid',
            borderBottomWidth: '1px',
            borderBottomColor: '#f1f5f9',
            backgroundColor: '#ffffff',
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: '#f4f9fa',
            },
        },
        stripedStyle: {
            backgroundColor: '#fbfcfd',
        },
    },
    cells: {
        style: {
            fontSize: '13px',
            color: '#374151',
            justifyContent: 'flex-start',
            paddingLeft: '14px',
            paddingRight: '14px',
        },
    },
    pagination: {
        style: {
            color: '#374151',
            fontSize: '13px',
            minHeight: '56px',
            backgroundColor: '#ffffff',
            borderTopStyle: 'solid',
            borderTopWidth: '1px',
            borderTopColor: '#f1f5f9',
        },
        pageButtonsStyle: {
            borderRadius: '50%',
            height: '34px',
            width: '34px',
            padding: '6px',
            margin: '0 2px',
            cursor: 'pointer',
            transition: '0.2s',
            color: ACCENT_DARK,
            fill: ACCENT_DARK,
            backgroundColor: 'transparent',
            '&:disabled': {
                cursor: 'unset',
                color: '#e2e8f0',
                fill: '#e2e8f0',
            },
            '&:hover:not(:disabled)': {
                backgroundColor: '#e9f2f4',
            },
            '&:focus': {
                outline: 'none',
                backgroundColor: '#e9f2f4',
            },
        },
    },
};

const statusBadgeStyles = {
    COMPLETED: { bg: '#e8f5ee', color: '#198754' },
    PENDING: { bg: '#fff8e6', color: '#b8860b' },
    IN_PROCESS: { bg: '#e8f1fb', color: '#2563eb' },
    FAILED: { bg: '#fdecea', color: '#c0392b' },
};

const StatusBadge = ({ status }) => {
    if (!status) return <span>—</span>;
    const { bg, color } = statusBadgeStyles[status] || { bg: '#f1f3f5', color: '#64748b' };
    return (
        <span
            className="text-truncate"
            style={{
                backgroundColor: bg,
                color,
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.03em',
                padding: '4px 10px',
                borderRadius: '999px',
                textTransform: 'uppercase',
                display: 'inline-block',
                maxWidth: '100%',
            }}
        >
            {status.replace('_', ' ')}
        </span>
    );
};

const statusOptions = [
    { value: "PENDING", label: "PENDING" },
    { value: "IN_PROCESS", label: "IN PROCESS" },
    { value: "COMPLETED", label: "COMPLETED" },
    { value: "FAILED", label: "FAILED" }

];

const defaultColumns = {
    's_no': true,
    'Client': true,
    'Facility': true,
    'Lockbox Details': true,
    'Status': true,
    'Initiated Time': true,
    'Start Time': true,
    'End Time': true,
    'Total Time': true,
    'Created By': true,
    'Deposit Date': true,
   
};

const FileMergeSchedulerLog = () => {
    const [colorRow, setColorRow] = useState({});
    const [toastMessage, setToastMessage] = useState({ status: false, message: "", type: "" });
    const [resetCount, setResetCount] = useState(0);
    const [sortColumn, setSortColumn] = useState("");
    const [sortOrder, setSortOrder] = useState("");
    const [userlist, setUserlist] = useState([]);

    const [selectedPk, setSelectedPk] = useState(null);
    const [isSchedulerLogOpen, setIsSchedulerLogOpen] = useState(false);
 
    const [selectedMergedFiles, setSelectedMergedFiles] = useState(null);
    const [isMergedFilesOpen, setIsMergedFilesOpen] = useState(false);
    const [mergedFilesSearch, setMergedFilesSearch] = useState("");
    const [copiedFileIndex, setCopiedFileIndex] = useState(null);

    const handleOpenMergedFiles = (mergedFiles) => {
        setSelectedMergedFiles(mergedFiles);
        setMergedFilesSearch("");
        setCopiedFileIndex(null);
        setIsMergedFilesOpen(true);
    };

    const handleCloseMergedFiles = () => {
        setIsMergedFilesOpen(false);
        setMergedFilesSearch("");
        setCopiedFileIndex(null);
    };

    const getFileIconMeta = (filename) => {
        const ext = String(filename).split('.').pop()?.toLowerCase();
        if (ext === 'pdf') return { Icon: BsFileEarmarkPdf, color: '#c0392b', bg: '#fdecea' };
        if (['tif', 'tiff', 'png', 'jpg', 'jpeg', 'gif', 'bmp'].includes(ext)) return { Icon: BsFileEarmarkImage, color: ACCENT, bg: '#eaf3f5' };
        if (['zip', 'rar', '7z'].includes(ext)) return { Icon: BsFileEarmarkZip, color: '#b8860b', bg: '#fdf6e3' };
        if (['xls', 'xlsx', 'csv'].includes(ext)) return { Icon: BsFileEarmarkSpreadsheet, color: '#198754', bg: '#e8f5ee' };
        return { Icon: BsFileEarmarkText, color: '#64748b', bg: '#f1f3f5' };
    };

    const getFilesArray = (mergedFiles) => {
        if (!mergedFiles) return [];
        let files = mergedFiles;
        if (typeof mergedFiles === 'string') {
            try {
                files = JSON.parse(mergedFiles);
            } catch (e) {
                return [mergedFiles];
            }
        }
        if (Array.isArray(files)) {
            return files;
        }
        if (typeof files === 'object') {
            return Object.values(files);
        }
        return [String(files)];
    };

    const handleCopyAllFiles = () => {
        const files = getFilesArray(selectedMergedFiles);
        const text = files.join(', ');
        navigator.clipboard.writeText(text);
        setToastMessage({ status: true, message: "All file names copied to clipboard!", type: "success" });
    };

    const handleCopySingleFile = (filename, index) => {
        navigator.clipboard.writeText(filename);
        setCopiedFileIndex(index);
        setTimeout(() => setCopiedFileIndex((current) => (current === index ? null : current)), 1500);
        setToastMessage({ status: true, message: `Copied "${filename}" to clipboard!`, type: "success" });
    };

    const handleOpenSchedulerLog = (pk) => {
        setSelectedPk(pk);
        setIsSchedulerLogOpen(true);
    };

    const [selectedDetailedLogId, setSelectedDetailedLogId] = useState(null);
    const [isDetailedLoggingOpen, setIsDetailedLoggingOpen] = useState(false);

    const handleOpenDetailedLogging = (id) => {
        setSelectedDetailedLogId(id);
        setIsDetailedLoggingOpen(true);
    };

    const handleCloseDetailedLogging = () => {
        setIsDetailedLoggingOpen(false);
        setSelectedDetailedLogId(null);
    };

    const [downloadingLogRowId, setDownloadingLogRowId] = useState(null);

    const handleDownloadLogFile = (row) => {
        const rowId = row.pk || row.id;
        setDownloadingLogRowId(rowId);
        downloadFullS3File({ file_path: row?.log_record || "" })((response) => {
            setDownloadingLogRowId(null);
            if (response?.status && response?.data?.signed_url) {
                handleDownloadFileUrl(response.data.signed_url);
            } else {
                setToastMessage({ status: true, message: "Failed to download log file.", type: "danger" });
            }
        });
    };

    const [data, setData] = useState({ results: { data: [] }, count: 0 });
    const [loading, setLoading] = useState(false);
    const [pageNo, setPageNo] = useState(1);
    const [limit, setLimit] = useState(25);
    const [totalRows, setTotalRows] = useState(0);
    const [searchedData, setSearchedData] = useState({});

    const pageNoRef = useRef(pageNo);
    const limitRef = useRef(limit);

    useEffect(() => {
        pageNoRef.current = pageNo;
        limitRef.current = limit;
    }, [pageNo, limit]);

    const [state, setState] = useState(defaultColumns);

    const filterFormik = useFormik({
        initialValues: {
            partner: [],
            client: [],
            facility: [],
            lockbox_file_name: "",
            file_name: "",
            status: [],
            user: [],
            created_date: "",
            created_date_range_picker_val: null,
            initiated_time: "",
            initiated_date_range_picker_val: null
        },
        onSubmit: (values) => {
            setPageNo(1);
            setSortColumn("");
            setSortOrder("");
            const searchParams = { ...values };
            setSearchedData(searchParams);
            fetchData(1, limit, searchParams, "", "");
        }
    });

    const fetchData = (page, pageLimit, filters, sortKey, sortDir) => {
        setLoading(true);
        const props = {
            partner: filters?.partner || [],
            client: filters?.client || [],
            facility: filters?.facility || [],
            lockbox_file_name: filters?.lockbox_file_name || "",
            file_name: filters?.file_name || "",
            status: filters?.status || [],
            user: filters?.user || [],
            deposit_date: filters?.created_date || "",
            initiated_time: filters?.initiated_time || "",
            sort_key: sortKey || "",
            sort_order: sortDir || ""
        };

        getFileMergeSchedulerLogs(page, pageLimit, props)((response) => {
            setLoading(false);
            if (!response) {
                setData({ results: { data: [] }, count: 0 });
                setTotalRows(0);
                return;
            }

            let count = 0;
            let resultsData = [];

            // Robust parsing logic to handle all wrapper styles
            if (response.data) {
                count = response.data.count || 0;
                if (response.data.results && Array.isArray(response.data.results.data)) {
                    resultsData = response.data.results.data;
                } else if (Array.isArray(response.data.results)) {
                    resultsData = response.data.results;
                } else if (Array.isArray(response.data)) {
                    resultsData = response.data;
                    count = response.data.length;
                }
            } else if (response.results) {
                count = response.count || 0;
                if (Array.isArray(response.results.data)) {
                    resultsData = response.results.data;
                } else if (Array.isArray(response.results)) {
                    resultsData = response.results;
                }
            } else if (response.status && response.data) {
                const inner = response.data;
                count = inner.count || 0;
                if (inner.results && Array.isArray(inner.results.data)) {
                    resultsData = inner.results.data;
                } else if (Array.isArray(inner.results)) {
                    resultsData = inner.results;
                } else if (Array.isArray(inner.data)) {
                    resultsData = inner.data;
                }
            } else if (Array.isArray(response)) {
                resultsData = response;
                count = response.length;
            }

            setData({ results: { data: resultsData }, count: count });
            setTotalRows(count);
        });
    };

    useEffect(() => {
        fetchData(pageNo, limit, searchedData, sortColumn, sortOrder);
        getUserlist(1)((response) => {
            setUserlist(response?.data?.results || []);
        });
    }, []);

    const handleCustomReset = () => {
        filterFormik.resetForm();
        setSearchedData({});
        setPageNo(1);
        setSortColumn("");
        setSortOrder("");
        setResetCount(prev => prev + 1);
        fetchData(1, limit, {}, "", "");
    };

    const handleSort = (column, sortDirection) => {
        const direction = sortDirection?.toLowerCase() === 'asc' ? 'asc' : 'desc';
        setSortColumn(column.id);
        setSortOrder(direction);
        setPageNo(1);
        fetchData(1, limit, searchedData, column.sort_key || column.id, direction);
    };

    const handlePageChange = (page) => {
        setPageNo(page);
        fetchData(page, limit, searchedData, sortColumn, sortOrder);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        setLimit(newPerPage);
        setPageNo(1);
        fetchData(1, newPerPage, searchedData, sortColumn, sortOrder);
        setResetCount(prev => prev + 1);
    };

    const handleOnDateTimeChange = (value) => {
        if (value) {
            const datefrom = moment(value[0]).format('MM/DD/YYYY HH:mm:ss');
            const dateto = moment(value[1]).format('MM/DD/YYYY HH:mm:ss');
            const range_str = `${datefrom} - ${dateto}`;
            filterFormik.setFieldValue("initiated_time", range_str);
            filterFormik.setFieldValue("initiated_date_range_picker_val", value);
        } else {
            filterFormik.setFieldValue("initiated_time", "");
            filterFormik.setFieldValue("initiated_date_range_picker_val", null);
        }
    };

    const handleOnCreatedDateChange = (value) => {
        if (value) {
            const datefrom = moment(value[0]).format('MM/DD/YYYY');
            const dateto = moment(value[1]).format('MM/DD/YYYY');
            const range_str = `${datefrom} - ${dateto}`;
            filterFormik.setFieldValue("created_date", range_str);
            filterFormik.setFieldValue("created_date_range_picker_val", value);
        } else {
            filterFormik.setFieldValue("created_date", "");
            filterFormik.setFieldValue("created_date_range_picker_val", null);
        }
    };

    const conditionalRowStyles = [
        {
            when: row => (colorRow.id !== undefined && colorRow.id === row.id) || (colorRow.pk !== undefined && colorRow.pk === row.pk),
            style: {
                backgroundColor: 'rgb(219, 244, 245)',
            },
            classNames: ['fml-row-selected'],
        },
    ];

    const columns = useMemo(
        () => [
            {
                id: 's_no',
                name: 'S.NO',
                selector: (row, index) => pageNoRef.current === 1 ? index + 1 : (pageNoRef.current - 1) * limitRef.current + index + 1,
                width: "70px",
                omit: state['s_no'] === false,
            },
            {
                id: 'client_name',
                name: 'Client',
                sort_key: 'client_name',
                selector: row => row?.client_name || "",
                sortable: true,
                cell: (row) => (
                    <div onClick={() => setColorRow(row)} className="text-truncate" style={{ width: "100%" }}>
                        <Tooltip disableInteractive title={row?.client_name || ""} placement="top" arrow>
                            <span className="text-truncate">{row?.client_name || "—"}</span>
                        </Tooltip>
                    </div>
                ),
                width: "160px",
                omit: state['Client'] === false,
            },
            {
                id: 'facility_name',
                name: 'Facility',
                sort_key: 'facility_name',
                selector: row => row?.facility_name || "",
                sortable: true,
                cell: (row) => (
                    <div onClick={() => setColorRow(row)} className="text-truncate" style={{ width: "100%" }}>
                        <Tooltip disableInteractive title={row?.facility_name || ""} placement="top" arrow>
                            <span className="text-truncate">{row?.facility_name || "—"}</span>
                        </Tooltip>
                    </div>
                ),
                width: "160px",
                omit: state['Facility'] === false,
            },
            {
                id: 'lockbox_details',
                name: 'Lockbox Details',
                sort_key: 'lockbox_details',
                selector: row => row?.lockbox_details || "",
                sortable: true,
                cell: (row) => (
                    <div onClick={() => setColorRow(row)} className="text-truncate" style={{ width: "100%" }}>
                        <Tooltip disableInteractive title={row?.lockbox_details || ""} placement="top" arrow>
                            <span className="text-truncate" >{row?.lockbox_details || "—"}</span>
                        </Tooltip>
                    </div>
                ),
                width: "180px",
                omit: state['Lockbox Details'] === false,
            },
            {
                id: 'status',
                name: 'Status',
                sort_key: 'status',
                selector: row => row?.status || "",
                sortable: true,
                cell: (row) => (
                    <div onClick={() => setColorRow(row)} className="text-truncate" style={{ width: "100%" }}>
                        <Tooltip disableInteractive title={row?.status || ""} placement="top" arrow>
                            <span><StatusBadge status={row?.status} /></span>
                        </Tooltip>
                    </div>
                ),
                width: "140px",
                omit: state['Status'] === false,
            },
            {
                id: 'initiated_time',
                name: 'Initiated Time',
                sort_key: 'initiated_time',
                selector: row => row?.initiated_time || "",
                sortable: true,
                cell: (row) => (
                    <div onClick={() => setColorRow(row)}>
                        <span>{row?.initiated_time || "—"}</span>
                    </div>
                ),
                width: "160px",
                omit: state['Initiated Time'] === false,
            },
            {
                id: 'start_time',
                name: 'Start Time',
                sort_key: 'start_time',
                selector: row => row?.start_time || "",
                sortable: true,
                cell: (row) => (
                    <div onClick={() => setColorRow(row)}>
                        <span>{row?.start_time || "—"}</span>
                    </div>
                ),
                width: "160px",
                omit: state['Start Time'] === false,
            },
            {
                id: 'end_time',
                name: 'End Time',
                sort_key: 'end_time',
                selector: row => row?.end_time || "",
                sortable: true,
                cell: (row) => (
                    <div onClick={() => setColorRow(row)}>
                        <span>{row?.end_time || "—"}</span>
                    </div>
                ),
                width: "160px",
                omit: state['End Time'] === false,
            },
            {
                id: 'total_time',
                name: 'Total Time',
                sort_key: 'total_time',
                selector: row => row?.total_time || "",
                sortable: true,
                cell: (row) => (
                    <div onClick={() => setColorRow(row)} className="fw-medium">
                        <span>{row?.total_time || "—"}</span>
                    </div>
                ),
                width: "120px",
                omit: state['Total Time'] === false,
            },
            {
                id: 'created_by',
                name: 'Created By',
                sort_key: 'created_by',
                selector: row => row?.created_by || "",
                sortable: true,
                cell: (row) => (
                    <div onClick={() => setColorRow(row)} className='text-truncate' style={{ width: "120px", overflow: 'hidden' }}>
                        <Tooltip disableInteractive title={row?.created_by || "—"} placement='top' arrow>
                            <span className='text-truncate'>
                                {row?.created_by || "—"}
                            </span>
                        </Tooltip>
                    </div>
                ),
                width: "120px",
                omit: state['Created By'] === false,
            },
            {
                id: 'created_date',
                name: 'Deposit date',
                sort_key: 'created_date',
                selector: row => row?.deposit_date || "",
                sortable: true,
                cell: (row) => (
                    <div onClick={() => setColorRow(row)}>
                        <span>{row?.deposit_date || "—"}</span>
                    </div>
                ),
                width: "160px",
                omit: state['Deposit Date'] === false,
            },
            {
                id: 'action',
                name: 'Action',
                cell: (row) => (
                    <div className="d-flex align-items-center gap-1">
                        {/* <Tooltip disableInteractive title='Scheduler Log' placement='top' arrow>
                            <Link className='d-flex align-items-center' style={{ fontSize: '1.5em', height: '32px' }} onClick={() => handleOpenSchedulerLog(row.pk || row.id)} >
                                <BsClockHistory color={"#000000"} />
                            </Link>
                        </Tooltip> */}
                        {row?.merged_files && Object.keys(row.merged_files).length > 0 && (
                            <Tooltip disableInteractive title='View Merged Files' placement='top' arrow>
                                <Link className='fml-action-btn' onClick={() => handleOpenMergedFiles(row.merged_files)} >
                                    <BsFileEarmarkText color={ACCENT_DARK} size={16} />
                                </Link>
                            </Tooltip>
                        )}
                        <Tooltip disableInteractive title='Detailed Logging' placement='top' arrow>
                            <Link className='fml-action-btn' onClick={() => handleOpenDetailedLogging(row.id || row.pk)} >
                                <BsClockHistory color={ACCENT_DARK} size={16} />
                            </Link>
                        </Tooltip>
                        {row?.log_record && (
                            <Tooltip disableInteractive title='Download Log File' placement='top' arrow>
                                <Link className='fml-action-btn' onClick={() => !downloadingLogRowId && handleDownloadLogFile(row)} >
                                    {downloadingLogRowId === (row.pk || row.id) ? (
                                        <Spinner animation="border" size="sm" style={{ width: 14, height: 14, color: ACCENT_DARK }} />
                                    ) : (
                                        <BsDownload color={ACCENT_DARK} size={16} />
                                    )}
                                </Link>
                            </Tooltip>
                        )}
                    </div>
                ),
                width: "180px",
                omit: state['Action'] === false,
            }
        ],
        [state, downloadingLogRowId]
    );

    return (
        <React.Fragment>
            <AdminMaster ToastMessage={toastMessage} />

            <ToastContainer className="p-3" position={"top-end"}>
                <Toast
                    bg={toastMessage?.type}
                    onClose={() => setToastMessage({ status: false, message: "", type: "" })}
                    show={toastMessage?.status}
                    animation={true}
                    delay={2000}
                    autohide
                >
                    <Toast.Body><span className='text-white'>{toastMessage?.message}</span></Toast.Body>
                </Toast>
            </ToastContainer>

            <main id="main" className="main fml-page">
                <style>{`
                    .fml-page .tab-fixed-header {
                        position: relative !important;
                        height: auto !important;
                        padding: 14px 0 8px;
                        margin-bottom: 0;
                        box-shadow: none;
                    }
                    .fml-page .fml-header-icon {
                        width: 42px;
                        height: 42px;
                        border-radius: 12px;
                        background: linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex-shrink: 0;
                    }
                    .fml-page .fml-card {
                        border-radius: 16px;
                        border: 1px solid #eef2f5;
                        box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04);
                        overflow: hidden;
                    }
                    .fml-page .fml-action-btn {
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        width: 26px;
                        height: 26px;
                        border-radius: 6px;
                        transition: background-color 0.15s ease;
                    }
                    .fml-page .fml-action-btn:hover {
                        background-color: #eaf3f5;
                    }
                    .fml-page .fml-filter-field {
                        flex: 1 1 220px;
                        min-width: 200px;
                        max-width: 300px;
                    }
                    .fml-page .fml-filter-field > div,
                    .fml-page .fml-filter-field .basic-single,
                    .fml-page .fml-filter-field .select-search,
                    .fml-page .fml-filter-field .rs-picker {
                        width: 100% !important;
                    }
                    @media (max-width: 767px) {
                        .fml-page .fml-filter-field {
                            flex: 1 1 100%;
                            max-width: 100%;
                        }
                    }
                    .fml-page .rdt_TableRow.fml-row-selected,
                    .fml-page .fml-row-selected {
                        background-color: rgb(219, 244, 245) !important;
                    }
                `}</style>

                <div className="pagetitle tab-fixed-header d-flex align-items-center">
                    <div className="d-flex align-items-center gap-3 ms-4">
                        {/* <div className="fml-header-icon">
                            <BsFileEarmarkText color="#ffffff" size={19} />
                        </div> */}
                        <h1 id='page-title' className="mb-0">File Merge Logs</h1>
                    </div>
                </div>

                <section className="section mt-1">
                    <div className="row align-items-top gap-2 gap-md-5">
                        <div className="col-lg-12">
                            <div className="card fml-card mb-5 mb-xl-8">
                                <div className='card-header border-0 ms-3'>
                                    <FilterComponent
                                        formik={filterFormik}
                                        onReset={handleCustomReset}
                                        onSearch={filterFormik.handleSubmit}
                                        extraAction={(
                                            <div className="d-flex align-items-center">
                                                <div className="me-2">
                                                    <TableHeadings
                                                        columnState={state}
                                                        setColumnState={setState}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    >
                                        <div className="fml-filter-field">
                                            <ClientDropDown lockboxformik={filterFormik} name="client" />
                                        </div>
                                        <div className="fml-filter-field">
                                            <FacilityDropDown lockboxformik={filterFormik} name="facility" clients="client" />
                                        </div>

                                        <div className="fml-filter-field">
                                            <Form.Control
                                                type="search"
                                                placeholder="Lockbox File Name"
                                                name="lockbox_file_name"
                                                value={filterFormik.values.lockbox_file_name}
                                                onChange={filterFormik.handleChange}
                                                className="form-control form-control-solid b-r-10"
                                                style={{
                                                    backgroundColor: "hsl(204deg 33.33% 97.06%)",
                                                    borderStyle: "none",
                                                    boxShadow: "none",
                                                    borderRadius: "10px",
                                                    height: "38px"
                                                }}
                                            />
                                        </div>

                                        <div className="fml-filter-field">
                                            <Form.Control
                                                type="search"
                                                placeholder="File Name"
                                                name="file_name"
                                                value={filterFormik.values.file_name}
                                                onChange={filterFormik.handleChange}
                                                className="form-control form-control-solid b-r-10"
                                                style={{
                                                    backgroundColor: "hsl(204deg 33.33% 97.06%)",
                                                    borderStyle: "none",
                                                    boxShadow: "none",
                                                    borderRadius: "10px",
                                                    height: "38px"
                                                }}
                                            />
                                        </div>

                                        <div className="fml-filter-field">
                                            <Select
                                                isMulti
                                                options={statusOptions}
                                                placeholder="Status"
                                                className="select-search"
                                                classNamePrefix="select"
                                                name="status"
                                                onChange={(options) => filterFormik.setFieldValue("status", options ? options.map(o => o.value) : [])}
                                                value={statusOptions.filter(o => filterFormik.values.status?.includes(o.value))}
                                                styles={customReactSelectStyles}
                                            />
                                        </div>

                                        <div className="fml-filter-field">
                                            <Select
                                                isMulti
                                                options={userlist}
                                                placeholder="User"
                                                className="select-search"
                                                classNamePrefix="select"
                                                name="user"
                                                onChange={(options) => filterFormik.setFieldValue("user", options ? options.map(o => o.value) : [])}
                                                value={userlist?.filter(o => filterFormik.values.user?.includes(o.value))}
                                                styles={customReactSelectStyles}
                                            />
                                        </div>

                                        <div className="fml-filter-field">
                                            <Tooltip title="Initiated Date Range" placement="top" disableInteractive>
                                                <Stack direction="column" spacing={8} alignItems="flex-start" style={{ width: '100%' }}>
                                                    <DateRangePicker
                                                        ranges={predefinedBottomRanges}
                                                        cleanable={true}
                                                        placement="auto"
                                                        defaultCalendarValue={[new Date().setHours(0, 0, 0, 0), new Date().setHours(23, 59, 59, 59)]}
                                                        appearance="default"
                                                        format={"MM/dd/yyyy HH:mm"}
                                                        placeholder={"MM/DD/YYYY - MM/DD/YYYY"}
                                                        className="form-date-control form-control-solid b-r-1 text-dark jobmonitor-datepicker"
                                                        style={{ width: '100%' }}
                                                        name="initiated_date_range_picker_val"
                                                        onChange={(value) => { handleOnDateTimeChange(value) }}
                                                        value={filterFormik?.values?.initiated_date_range_picker_val}
                                                    />
                                                </Stack>
                                            </Tooltip>
                                        </div>

                                        <div className="fml-filter-field">
                                            <Tooltip title="Deposit date" placement="top" disableInteractive>
                                                <Stack direction="column" spacing={8} alignItems="flex-start" style={{ width: '100%' }}>
                                                    <DateRangePicker
                                                        ranges={predefinedBottomRanges}
                                                        cleanable={true}
                                                        placement="auto"
                                                        defaultCalendarValue={[new Date().setHours(0, 0, 0, 0), new Date().setHours(23, 59, 59, 59)]}
                                                        appearance="default"
                                                        format={"MM/dd/yyyy"}
                                                        placeholder={"MM/DD/YYYY - MM/DD/YYYY"}
                                                        className="form-date-control form-control-solid b-r-1 text-dark jobmonitor-datepicker"
                                                        style={{ width: '100%' }}
                                                        name="created_date_range_picker_val"
                                                        onChange={(value) => { handleOnCreatedDateChange(value) }}
                                                        value={filterFormik?.values?.created_date_range_picker_val}
                                                    />
                                                </Stack>
                                            </Tooltip>
                                        </div>
                                    </FilterComponent>
                                </div>

                                <div className="card-body" style={{ padding: "0px 1.25rem 1.25rem" }}>
                                    <div className="table-responsive">
                                        <div className="dashboardtable">
                                            <DataTable
                                                key={resetCount}
                                                className="custom-datatable"
                                                fixedHeader
                                                fixedHeaderScrollHeight="calc(100vh - 280px)"
                                                data={data?.results?.data || []}
                                                columns={columns}
                                                striped
                                                progressPending={loading}
                                                progressComponent={<div style={{ fontWeight: 'bold', color: ACCENT_DARK }} className="p-5">Loading...</div>}
                                                noDataComponent={(
                                                    <div className="text-center py-5">
                                                        <BsFileEarmarkText size={26} color="#cbd5e1" />
                                                        <div className="mt-2" style={{ fontSize: "13px", color: "#94a3b8" }}>There are no records to display</div>
                                                    </div>
                                                )}
                                                pagination
                                                paginationServer
                                                paginationTotalRows={totalRows}
                                                onSort={handleSort}
                                                sortServer
                                                sortColumn={sortColumn}
                                                sortDirection={sortOrder}
                                                paginationPerPage={limit}
                                                paginationRowsPerPageOptions={[25, 50, 100]}
                                                onChangeRowsPerPage={handlePerRowsChange}
                                                onChangePage={handlePageChange}
                                                conditionalRowStyles={conditionalRowStyles}
                                                onRowClicked={setColorRow}
                                                theme="solarized"
                                                customStyles={mergeLogsTableStyles}
                                                pointerOnHover
                                                highlightOnHover
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>



                <SchedulerLogModal
                    show={isSchedulerLogOpen}
                    onClose={() => {
                        setIsSchedulerLogOpen(false);
                        setSelectedPk(null);
                    }}
                    pk={selectedPk}
                />

                <DetailedLoggingModal
                    show={isDetailedLoggingOpen}
                    onClose={handleCloseDetailedLogging}
                    id={selectedDetailedLogId}
                />

                <Modal
                    show={isMergedFilesOpen}
                    onHide={handleCloseMergedFiles}
                    centered
                    backdrop="static"
                    contentClassName="border-0 shadow-lg overflow-hidden"
                    style={{ borderRadius: "16px" }}
                >
                    {(() => {
                        const allFiles = getFilesArray(selectedMergedFiles);
                        const filteredFiles = mergedFilesSearch
                            ? allFiles.filter((f) => String(f).toLowerCase().includes(mergedFilesSearch.toLowerCase()))
                            : allFiles;

                        return (
                            <>
                                {/* Header */}
                                <div
                                    style={{
                                        background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`,
                                        padding: "20px 24px",
                                    }}
                                >
                                    <div className="d-flex align-items-start justify-content-between">
                                        <div className="d-flex align-items-center gap-3">
                                            <div
                                                className="d-flex align-items-center justify-content-center"
                                                style={{ width: 40, height: 40, borderRadius: "12px", backgroundColor: "rgba(255,255,255,0.16)" }}
                                            >
                                                <BsFileEarmarkText color="#ffffff" size={19} />
                                            </div>
                                            <div>
                                                <div className="fw-bold text-white" style={{ fontSize: "16px", lineHeight: 1.2 }}>Merged Files</div>
                                                <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.75)" }}>
                                                    {allFiles.length} file{allFiles.length !== 1 ? "s" : ""} combined into this batch
                                                </div>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            {allFiles.length > 1 && (
                                                <Tooltip title="Copy All File Names" placement="top" arrow>
                                                    <button
                                                        onClick={handleCopyAllFiles}
                                                        className="d-flex align-items-center gap-1 fw-semibold"
                                                        style={{
                                                            borderRadius: "8px",
                                                            border: "1px solid rgba(255,255,255,0.35)",
                                                            backgroundColor: "rgba(255,255,255,0.14)",
                                                            color: "#ffffff",
                                                            fontSize: "12.5px",
                                                            padding: "7px 12px",
                                                        }}
                                                    >
                                                        <BiCopy size={14} />
                                                        <span>Copy All</span>
                                                    </button>
                                                </Tooltip>
                                            )}
                                            <button
                                                onClick={handleCloseMergedFiles}
                                                className="d-flex align-items-center justify-content-center"
                                                style={{
                                                    width: 30,
                                                    height: 30,
                                                    borderRadius: "50%",
                                                    border: "none",
                                                    backgroundColor: "rgba(255,255,255,0.14)",
                                                    color: "#ffffff",
                                                }}
                                            >
                                                <BsX size={20} />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Search (only when the list is long enough to need it) */}
                                {allFiles.length > 6 && (
                                    <div style={{ padding: "12px 20px", borderBottom: "1px solid #f1f5f9", backgroundColor: "#ffffff" }}>
                                        <div className="d-flex align-items-center gap-2" style={{ backgroundColor: "#f8fafc", borderRadius: "8px", padding: "8px 12px" }}>
                                            <BiSearch color="#94a3b8" size={16} />
                                            <input
                                                type="search"
                                                value={mergedFilesSearch}
                                                onChange={(e) => setMergedFilesSearch(e.target.value)}
                                                placeholder="Filter files by name"
                                                style={{ border: "none", outline: "none", backgroundColor: "transparent", fontSize: "13px", width: "100%" }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* File list */}
                                <div style={{ maxHeight: "50vh", overflowY: "auto", padding: "14px 18px", backgroundColor: "#fbfcfd" }}>
                                    {filteredFiles.length === 0 ? (
                                        <div className="text-center text-muted py-5">
                                            <BsFileEarmarkText size={26} color="#cbd5e1" />
                                            <div className="mt-2" style={{ fontSize: "13px" }}>
                                                {allFiles.length === 0 ? "No merged files to display" : "No files match your search"}
                                            </div>
                                        </div>
                                    ) : (
                                        filteredFiles.map((filename, index) => {
                                            const { Icon, color, bg } = getFileIconMeta(filename);
                                            const isCopied = copiedFileIndex === index;
                                            return (
                                                <div
                                                    key={filename + index}
                                                    className="d-flex align-items-center gap-3"
                                                    style={{
                                                        backgroundColor: "#ffffff",
                                                        border: "1px solid #eef2f5",
                                                        borderRadius: "10px",
                                                        padding: "9px 12px",
                                                        marginBottom: "8px",
                                                    }}
                                                >
                                                    <div
                                                        className="d-flex align-items-center justify-content-center flex-shrink-0"
                                                        style={{ width: 22, height: 22, borderRadius: "50%", backgroundColor: "#f1f5f9", fontSize: "10.5px", fontWeight: 700, color: "#94a3b8" }}
                                                    >
                                                        {index + 1}
                                                    </div>
                                                    <div
                                                        className="d-flex align-items-center justify-content-center flex-shrink-0"
                                                        style={{ width: 32, height: 32, borderRadius: "8px", backgroundColor: bg }}
                                                    >
                                                        <Icon color={color} size={15} />
                                                    </div>
                                                    <Tooltip disableInteractive title={filename} placement="top" arrow>
                                                        <span
                                                            className="text-truncate flex-grow-1"
                                                            style={{ fontSize: "13px", color: "#1e293b", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" }}
                                                        >
                                                            {filename}
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title={isCopied ? "Copied!" : "Copy File Name"} placement="top" arrow>
                                                        <button
                                                            onClick={() => handleCopySingleFile(filename, index)}
                                                            className="d-flex align-items-center justify-content-center flex-shrink-0"
                                                            style={{
                                                                width: 28,
                                                                height: 28,
                                                                borderRadius: "7px",
                                                                border: "none",
                                                                backgroundColor: isCopied ? "#e8f5ee" : "#f1f5f9",
                                                            }}
                                                        >
                                                            {isCopied ? <BsCheck2 color="#198754" size={15} /> : <BiCopy color="#64748b" size={14} />}
                                                        </button>
                                                    </Tooltip>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>

                                {/* Footer */}
                                <div
                                    className="d-flex align-items-center justify-content-between"
                                    style={{ borderTop: "1px solid #f1f5f9", padding: "12px 20px", backgroundColor: "#ffffff" }}
                                >
                                    <span style={{ fontSize: "12px", color: "#94a3b8" }}>
                                        Showing {filteredFiles.length} of {allFiles.length} file{allFiles.length !== 1 ? "s" : ""}
                                    </span>
                                    <Button
                                        onClick={handleCloseMergedFiles}
                                        size="sm"
                                        style={{ borderRadius: "8px", backgroundColor: ACCENT, border: "none", padding: "6px 16px", fontSize: "13px" }}
                                    >
                                        Close
                                    </Button>
                                </div>
                            </>
                        );
                    })()}
                </Modal>
            </main>
        </React.Fragment>
    );
};

export default FileMergeSchedulerLog;
